<template>
  <v-alert
    v-if="errorMsg"
    class="error-msg"
    :text="errorMsg"
    type="error"
    variant="outlined"
    :closable="true"></v-alert>
  <v-container v-if="pageError" class="mt-8" data-testid="qualify-not-found">
    <EmptyState variant="error" :message="pageError" action-label="Go Back" @action="goBack" />
  </v-container>
  <div v-else class="d-flex flex-column align-center mt-8 intake-detail">
    <v-container :fluid="true" class="lh--container-wide">
      <v-alert
        v-if="
          !showLoader &&
          insuranceCards !== undefined &&
          insuranceCards.length === 0 &&
          !coverageSelected &&
          !earlierStep
        "
        :text="isManualEntry ? 'This order has no coverage to verify' : 'This patient has no insurance cards available'"
        type="error"
        variant="outlined"></v-alert>
      <div class="intake-detail__header">
        <h1 class="intake-detail__title">Select Active Insurance for Prior Authorization</h1>
        <span class="intake-detail__stage-chip">
          <span class="intake-detail__stage-dot" />
          Benefit Verification
        </span>
      </div>
      <ReadOnlyBanner v-if="isReadOnlyDevice" />
      <ReadOnlyBanner v-if="intakeStore.lockViewer" icon="mdi-account-lock-outline" :text="lockBannerText">
        <template v-if="lockFree && !isReadOnlyDevice" #action>
          <v-btn size="small" color="primary" variant="flat" @click="takeOverEditing">Take over editing</v-btn>
        </template>
      </ReadOnlyBanner>
      <v-alert v-if="earlierStep" type="info" variant="tonal" density="compact" class="mb-4">
        This is an earlier benefit verification of this order. It is closed and read only.
      </v-alert>
      <v-alert
        v-if="earlierStep && !showLoader && !stepCoverage"
        variant="tonal"
        density="compact"
        class="mb-4"
        text="No coverage confirmed at this step." />
      <IntakeStepNotice
        v-if="worklistEnabled && intakeOrder"
        step="benefits_verification"
        :status="intakeOrder.status"
        :order-id="intakeOrder.id"
        :order-info="intakeOrder.orderInfo" />
      <v-container :fluid="true" class="review-container">
        <v-row no-gutters>
          <v-col v-if="!showLoader" cols="12" md="7">
            <PatientBar
              v-if="patientInfo"
              :patient="patientInfo"
              :patient-guess="intakeOrder?.patientGuess"
              :disabled="true"
              :client-company="intakeOrder?.clientCompany.id"
              @highlight-bounding-box="onPatientGuessHighlight" />
          </v-col>
          <v-col v-if="!showLoader && !isManualEntry" cols="12" md="5">
            <ClassificationBar :patient-guess="intakeOrder?.patientGuess" />
          </v-col>
          <v-container v-if="showLoader">
            <v-skeleton-loader type="text@4" />
          </v-container>
        </v-row>
        <PagePreview
          v-if="
            (currentDocumentPage || (isManualEntry && lockSettled)) && !showLoader && requirementsStore.clientConfig
          "
          ref="previewRef"
          :no-document="isManualEntry"
          :read-only="!formWritable"
          :closed-step="earlierStep"
          :order-form="intakeOrder.orderForm"
          :insurance-cards="insuranceCards"
          :active-insurance-card-id="activeInsuranceCardId"
          @attribute-edit="onAttributeEdit"
          @select-insurance-card="onSelectInsuranceCard"
          @delete-content="onDeleteContent"
          @snooze="onSnooze"
          @archive="onArchive">
          <template v-if="canAct" #actions>
            <v-btn variant="tonal" size="small" rounded color="primary" class="mr-2" @click="mergeDialog = true">
              <v-icon size="16" start>mdi-call-merge</v-icon>
              Merge Orders
            </v-btn>
          </template>
          <template #before>
            <v-col v-if="awaitingOrderForm" cols="12">
              <v-alert type="info" variant="tonal" density="compact">
                Awaiting order — details arrive with the order fax
              </v-alert>
            </v-col>
            <v-col v-if="shouldShowReimbursementTable" cols="12">
              <ReimbursementDataCard :order="intakeOrder" />
            </v-col>
            <v-col v-if="coverageSelected" cols="12">
              <StepCoverageCard :coverage="stepCoverage" :selected="!earlierStep" />
            </v-col>
          </template>
        </PagePreview>
        <v-skeleton-loader v-if="showLoader || (currentDocumentPage === null && !isManualEntry)" type="image@8" />
        <div v-if="!isReadOnlyDevice" class="document-footer">
          <v-skeleton-loader :loading="showLoader" type="heading">
            <div class="intake-actionbar w-100">
              <v-btn variant="outlined" color="primary" rounded prepend-icon="mdi-arrow-left" @click="goBack">
                Go Back
              </v-btn>
              <div class="intake-actionbar__spacer" />
              <template v-if="canAct && (insuranceCards.length > 0 || coverageSelected)">
                <v-btn
                  v-if="!medicalNecessityEnabled"
                  color="primary"
                  variant="flat"
                  rounded
                  append-icon="mdi-arrow-right"
                  :disabled="!hasSelection || buttonDisabled"
                  :loading="showLoader"
                  @click="skipToPriorAuth">
                  Start Prior Authorization
                </v-btn>
                <v-btn
                  v-else-if="!checkPayorInNetwork"
                  color="primary"
                  variant="flat"
                  rounded
                  append-icon="mdi-arrow-right"
                  :disabled="!hasSelection"
                  @click="setCheckPayorInNetwork">
                  Check In-Network Status
                </v-btn>
              </template>
            </div>
          </v-skeleton-loader>
        </div>
      </v-container>
      <div v-if="checkPayorInNetwork && !showLoader && canAct" class="qualify-form">
        <form class="qualify-card" @submit.prevent="">
          <div class="qualify-card__header">
            <span class="qualify-card__title">Benefit Verification</span>
            <span
              v-if="intakeOrder?.insuranceCard || coverageSelected"
              class="qualify-network-chip"
              :class="payorInNetwork ? 'qualify-network-chip--in' : 'qualify-network-chip--out'">
              <span class="qualify-network-chip__dot" />
              {{ payorInNetwork ? "Payor in network" : "Payor not in network" }}
            </span>
          </div>
          <div class="qualify-card__hint">
            {{ coverageSelected ? "A coverage" : "An insurance card" }} has been selected. If you wish to proceed please
            {{ returnHomeAfterQualify ? "complete the benefit verification" : "continue to medical necessity" }} or
            select "No" to archive the order.
          </div>
          <div class="qualify-card__controls">
            <v-select
              v-model="continueProcessing"
              :items="['Yes', 'No']"
              label="Do you want to continue processing this patient?" />
            <v-select
              v-if="continueProcessing === 'No' && !isManualEntry"
              v-model="notifyProvider"
              :items="['Yes', 'No']"
              label="Do you want to notify the referring provider if you will not be processing this patient?" />
          </div>
          <div v-if="notifyProvider === 'Yes' && continueProcessing === 'No'" class="mt-4">
            <label for="qualify-patient-template"> If yes, please update notification template: </label>
            <OutgoingFaxEditor
              v-if="intakeOrder"
              title=""
              :types="[TEMPLATE_TYPES.INTAKE_DISCONTINUE_PROCESSING]"
              :cancel-button="false"
              send-button-text="Send Fax and Archive Order"
              :intake-order="intakeOrder"
              :to-fax-number="intakeOrder.orderForm?.providerFax"
              @on-complete="onNotifyProviderComplete" />
          </div>
        </form>
      </div>
      <div v-if="checkPayorInNetwork && !showLoader && canAct">
        <div class="intake-actionbar">
          <div class="intake-actionbar__spacer" />
          <v-btn
            v-if="continueProcessing === 'Yes'"
            color="primary"
            variant="flat"
            rounded
            append-icon="mdi-arrow-right"
            @click="submitStep">
            {{ returnHomeAfterQualify ? "Complete Benefit Verification" : "Continue to Medical Necessity" }}
          </v-btn>
          <v-btn
            v-if="continueProcessing === 'No'"
            color="error"
            variant="tonal"
            rounded
            prepend-icon="mdi-archive-outline"
            @click="onArchive">
            Archive Order
          </v-btn>
        </div>
      </div>
      <BenefitsVerificationCard
        v-if="intakeOrder"
        :anchor="{ type: 'intake_order', id: intakeOrder.id }"
        :patient="voicePatient"
        :prefill="benefitsPrefill"
        :readonly="!canAct || !benefitsWritable || (takesLock && !holdsLock)"
        @changed="onBenefitsChanged" />
      <IntakeTagBar
        v-if="intakeOrder"
        :tags="intakeOrder.statusTags"
        :tag-defs="availableTags"
        :tag-due-dates="intakeOrder.tagDueDates"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:tags="(t) => (intakeOrder.statusTags = t)"
        @saved="auditLogListRef?.showLatest()"
        @update:tag-due-dates="(d) => (intakeOrder.tagDueDates = d)" />
      <AssigneeBar
        v-if="intakeOrder"
        :assignees="intakeOrder.assignees || []"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:assignees="(a) => (intakeOrder.assignees = a)" />
      <GroupBar
        v-if="intakeOrder"
        :assigned-groups="intakeOrder.assignedGroups || []"
        entity-type="order"
        :entity-id="intakeOrder.id"
        @update:assigned-groups="(g) => (intakeOrder.assignedGroups = g)" />
      <SubstatusBar v-if="intakeOrder" entity-type="order" :entity-id="intakeOrder.id" />
      <AuditEntryList
        v-if="intakeOrder"
        ref="auditLogListRef"
        :intake-order-id="intakeOrder.id"
        :tag-defs="availableTags" />
    </v-container>
  </div>
  <v-dialog v-model="archiveReasonDialog" max-width="800">
    <ArchiveNote :intake-order="intakeOrder" @on-archived="onArchived" />
  </v-dialog>
  <MergeOrdersDialog
    v-if="intakeOrder"
    v-model="mergeDialog"
    :order-id="intakeOrder.id"
    :patient-id="patientInfo?.id"
    :patient-name="patientName"
    :drug-name="orderDrugName"
    @merged="onMerged" />
</template>

<script setup>
// Core imports
import { useMarkCaseOpened } from "@/composables/useCaseReadState";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { useDate, useGoTo } from "vuetify";

// Component imports
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import AuditEntryList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import ArchiveNote from "@/components/ArchiveNote.vue";
import ReimbursementDataCard from "@/components/ReimbursementDataCard.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import BenefitsVerificationCard from "@/components/benefits/BenefitsVerificationCard.vue";
import IntakeStepNotice from "@/components/intakeReview/IntakeStepNotice.vue";
import StepCoverageCard from "@/components/intakeReview/StepCoverageCard.vue";
import MergeOrdersDialog from "@/components/intakeReview/MergeOrdersDialog.vue";
import EmptyState from "@/components/dashboard/EmptyState.vue";
import { useEditLock } from "@/composables/useEditLock";
import { useIntakeActions } from "@/composables/useIntakeActions";
import { useIntakeBenefitsCard } from "@/composables/useIntakeBenefitsCard";
import { useResponsive } from "@/composables/useResponsive";
import { useIntakeListFiltersStore } from "@/stores/intakeListFiltersStore";
import { drugNameOf } from "@/utils/openOrders";

// Service and model imports
import {
  INTAKE_STAGE,
  IntakeDocumentPage,
  IntakeInsuranceCard,
  IntakeOrder,
  Patient,
  clearIntakeSaveErrorHandler,
  setIntakeSaveErrorHandler,
} from "@/models/intake";
import { stepRoute } from "@/models/intakeSteps";
import { intakeService } from "@/services/intakeService";
import { clinicalReviewSubmitService, SubmitAbandoned } from "@/services/clinicalReviewSubmitService";
import router from "@/router";
import { useIntakeStore } from "@/stores/intakeStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { handleFormValidationErrors } from "@/utils/validators";
import { localDateToIso } from "@/utils/dateUtils";

const intakeActions = useIntakeActions();
const requirementsStore = useRequirementsStore();
const intakeStore = useIntakeStore();
const filtersStore = useIntakeListFiltersStore();
const dateAdapter = useDate();
const showLoader = ref(true);
const previewRef = ref(null);
const auditLogListRef = ref(null);
const intakeOrder = computed(() => intakeStore.intakeOrder);
const insuranceCards = computed(() => intakeStore.insuranceCards);
const currentDocumentPage = ref(null);
const activeInsuranceCardId = ref(null);
const pageChanger = intakeStore.pageManager;
const errorMsg = ref(null);
const patientInfo = ref(null);
const mergeDialog = ref(false);
const payorInNetwork = ref(false);
const continueProcessing = ref("Yes");
const notifyProvider = ref("No");
const checkPayorInNetwork = ref(false);
const buttonDisabled = ref(false);
const medicalNecessityEnabled = computed(() => requirementsStore.clientConfig?.medicalNecessityEnabled ?? true);
// When set, this client splits intake across teams: completing Qualify returns home instead of
// advancing into Clinical Review (LAM-3943). Defaults to false to preserve the combined flow.
const returnHomeAfterQualify = computed(() => requirementsStore.clientConfig?.intake?.returnHomeAfterQualify ?? false);
const availableTags = computed(() => requirementsStore.clientConfig?.intake?.availableTags || []);
const voicePatient = computed(() =>
  patientInfo.value
    ? {
        firstName: patientInfo.value.firstName,
        lastName: patientInfo.value.lastName,
        dob: patientInfo.value.dob ? localDateToIso(patientInfo.value.dob) : "",
      }
    : null
);
const worklistEnabled = computed(() => requirementsStore.clientConfig?.worklistEnabled ?? false);
const pageError = ref(null);
const lockSettled = ref(false);
const bvStep = ref(null);
const {
  holds: holdsLock,
  free: lockFree,
  bannerText: lockBannerText,
  start: startEditLock,
  stop: stopEditLock,
  takeOver,
  demote,
} = useEditLock(storeToRefs(intakeStore).lockViewer);
const isManualEntry = computed(
  () => Boolean(intakeOrder.value?.orderForm) && !intakeOrder.value.orderForm.intakeDocumentKey
);
// This order opened before its order form arrived, so the order form card has nothing to render.
const awaitingOrderForm = computed(() => Boolean(intakeOrder.value) && !intakeOrder.value.orderForm);
const stepCoverage = computed(() => bvStep.value?.coverage ?? null);
const coverageSelected = computed(() =>
  Boolean(intakeOrder.value && !intakeOrder.value.insuranceCard && stepCoverage.value)
);
const hasSelection = computed(() => Boolean(activeInsuranceCardId.value) || coverageSelected.value);
const earlierStep = computed(() => Boolean(route.query.bv) && bvStep.value?.latest === false);
const canAct = computed(() => !isReadOnlyDevice.value && !intakeStore.isReadOnly && !earlierStep.value);
const takesLock = computed(
  () => isManualEntry.value && !isReadOnlyDevice.value && !intakeStore.readOnly && !earlierStep.value
);
const formWritable = computed(() => !earlierStep.value && (!isManualEntry.value || holdsLock.value));
const mayEditForm = computed(() => formWritable.value && !intakeStore.isReadOnly);
const coverageAsCard = () =>
  coverageSelected.value
    ? { payorName: stepCoverage.value.insuranceProvider, memberId: stepCoverage.value.memberId }
    : null;
const { benefitsPrefill, benefitsWritable, onBenefitsChanged } = useIntakeBenefitsCard({
  orderId: () => route.params.id,
  order: () => intakeOrder.value,
  insuranceCard: () => insuranceCards.value?.find((c) => c.id === activeInsuranceCardId.value) ?? coverageAsCard(),
  onRefreshed: () => auditLogListRef.value?.refresh(),
  onError: (message) => (errorMsg.value = message),
});
const archiveReasonDialog = ref(false);

const route = useRoute();
useMarkCaseOpened("intakeOrderId", () => route.params.id);
const scrollTo = useGoTo();
const { isReadOnlyDevice } = useResponsive();

let unmounted = false;
let loads = 0;

const staleFor = (run) => () => unmounted || run !== loads;

const load = async () => {
  const stale = staleFor(++loads);
  showLoader.value = true;
  buttonDisabled.value = false;
  lockSettled.value = false;
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.QUALIFY_PATIENT;
  currentDocumentPage.value = null;
  checkPayorInNetwork.value = false;
  await fetchIntakeOrder(stale);
  if (stale()) return;
  if (!intakeOrder.value || pageError.value) {
    await stopEditLock();
    return;
  }
  await requirementsStore.refreshClientConfig(intakeOrder.value.clientCompany.id);
  if (stale()) return;
  if (isManualEntry.value) setIntakeSaveErrorHandler(onSaveError);
  else clearIntakeSaveErrorHandler(onSaveError);
  if (takesLock.value) {
    const formId = intakeOrder.value.orderForm.id;
    await startEditLock(`intake_order_form:${formId}`, intakeService.orderFormLock(formId)).catch(() => {
      errorMsg.value = "Could not take the edit lock. Reload the page.";
    });
  } else {
    await stopEditLock();
  }
  if (!stale()) lockSettled.value = true;
};

const onBeforeUnload = () => {
  if (!mayEditForm.value) return;
  insuranceCards.value?.map((card) => card.saveUpdates());
  intakeOrder.value?.saveUpdates();
};

onMounted(async () => {
  window.addEventListener("beforeunload", onBeforeUnload);
  await load();
  if (!unmounted) resumePendingSubmit();
});

onUnmounted(() => {
  unmounted = true;
  window.removeEventListener("beforeunload", onBeforeUnload);
  clearIntakeSaveErrorHandler(onSaveError);
  requirementsStore.refreshCurrentUserClientConfig();
});

watch(
  () => [route.params.id, route.query.bv],
  () => route.name === "qualify-order" && load()
);

const onSaveError = (e) => {
  const wasEditing = !intakeStore.lockViewer;
  if (!demote(e)) throw e;
  if (wasEditing) errorMsg.value = "This record is now being edited by another user.";
};

const takeOverEditing = async () => {
  const stale = staleFor(loads);
  try {
    if (!(await takeOver())) return;
  } catch (e) {
    errorMsg.value = "Could not take over editing. Try again.";
    return;
  }
  if (stale()) return;
  errorMsg.value = "";
  showLoader.value = true;
  await fetchIntakeOrder(stale);
};

const markAsActiveInsurance = async (card) => {
  errorMsg.value = "";
  const orderId = intakeOrder.value.id;
  const moved = () => intakeOrder.value?.id !== orderId;
  try {
    const order = await intakeService.patchIntakeOrder(orderId, { insuranceCardId: card.id });
    if (moved()) return;
    intakeStore.intakeOrder = new IntakeOrder(order, dateAdapter);
    activeInsuranceCardId.value = intakeStore.intakeOrder.insuranceCard?.id;
    updatePayorInNetwork();
  } catch (e) {
    if (!moved()) errorMsg.value = "Failed to mark insurance card as active";
  }
};

const updatePayorInNetwork = () => {
  const card = insuranceCards.value.find((card) => card.id === activeInsuranceCardId.value) ?? coverageAsCard();
  payorInNetwork.value = (intakeOrder.value.payorNames ?? []).indexOf(card?.payorName) !== -1;
};

const onSelectInsuranceCard = async (cardId) => {
  const card = insuranceCards.value.find((c) => c.id === cardId);
  pageChanger.goToDataFormPage(card);
  checkPayorInNetwork.value = false;
  await markAsActiveInsurance(card);
};

const onAttributeEdit = (field, value, type, id) => {
  if (!mayEditForm.value) return;
  if (type === "ORDER_FORM") {
    intakeOrder.value.orderForm.updateAttribute(field.key, value, type, id);
    intakeOrder.value.orderForm.debouncedSaveUpdates();
  } else if (type === "INSURANCE_CARD") {
    const card = insuranceCards.value.find((card) => card.id === id);
    if (card) {
      card.updateAttribute(field.key, value, type);
      card.debouncedSaveUpdates();
      if (field.key === "payorName") {
        updatePayorInNetwork();
      }
    }
  }
};

const onDeleteContent = async (content, documentType) => {
  if (!(await intakeActions.confirmDeleteContent(documentType))) {
    return;
  }
  if (!(await saveAnyPendingData())) {
    return;
  }
  const stale = staleFor(loads);
  errorWrapper(async () => {
    await intakeActions.onDeleteContentConfirm(content, documentType);
    await fetchIntakeOrder(stale);
  }, "Error deleting the content");
};

const saveAnyPendingData = async () => {
  return await errorWrapper(async () => {
    await Promise.all([...insuranceCards.value.map((card) => card.saveUpdates()), intakeOrder.value.saveUpdates()]);
  }, "Error saving any pending data");
};

const setCheckPayorInNetwork = () => {
  checkPayorInNetwork.value = true;
  scrollTo(".document-footer", { behavior: "smooth", duration: 1000 });
};

const showingThisOrder = () => {
  const shown = String(route.params.id);
  return () => !unmounted && String(route.params.id) === shown;
};

const resumePendingSubmit = async () => {
  const orderId = route.params.id;
  const current = showingThisOrder();
  const pending = await clinicalReviewSubmitService.pending(orderId, { isActive: current });
  if (!current() || pending?.status !== "processing") return;
  showLoader.value = true;
  buttonDisabled.value = true;
  try {
    await clinicalReviewSubmitService.wait(orderId, { isActive: current, submitId: pending.submitId });
    if (current()) router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
  } catch (ex) {
    if (current() && !(ex instanceof SubmitAbandoned)) errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    if (current()) {
      showLoader.value = false;
      buttonDisabled.value = false;
    }
  }
};

const skipToPriorAuth = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  if (previewRef.value.formErrors.length > 0) {
    errorMsg.value = "There are errors in the insurance card. Please make sure that all required fields are filled out";
    showLoader.value = false;
    buttonDisabled.value = false;
    return;
  }
  const order = intakeOrder.value;
  const current = showingThisOrder();
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await order.saveUpdates();
    await clinicalReviewSubmitService.submit(order.id, {}, { isActive: current });
    // Back to the intake list; depending on submission method (i.e. CoverMyMeds) some orders will
    // require standby time before status will switch from Created to Requires Review
    if (current()) router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
  } catch (ex) {
    if (current() && !(ex instanceof SubmitAbandoned)) errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    if (current()) {
      showLoader.value = false;
      buttonDisabled.value = false;
    }
  }
};

const submitStep = async () => {
  const orderId = intakeOrder.value.id;
  const current = showingThisOrder();
  showLoader.value = true;
  errorMsg.value = "";
  const isValid = await handleFormValidationErrors(previewRef.value.formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
    showLoader.value = false;
    return;
  }
  if (!intakeOrder.value.insuranceCard && !coverageSelected.value) {
    errorMsg.value = "No active insurance card selected";
    showLoader.value = false;
    return;
  }
  try {
    // Submit the order to clinical review
    await Promise.all(insuranceCards.value.map((card) => card.saveUpdates()));
    await intakeOrder.value.saveUpdates();
    await intakeService.postOrderSubmitQualify(intakeOrder.value.id);
    if (!current()) return;
    if (returnHomeAfterQualify.value) {
      // This client's Clinical Review is handled by a separate team (LAM-3943); return home.
      router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
    } else {
      router.push({ name: "document-review-order", params: { id: orderId } });
    }
  } catch (e) {
    if (current()) errorMsg.value = "Failed to submit the Draft Order";
  }
  if (current()) showLoader.value = false;
};

const onNotifyProviderComplete = ({ sent }) => {
  if (sent !== null) {
    onArchive();
  }
};

const fetchPatientQualifyDocuments = async (patientId, stale = () => false) => {
  errorMsg.value = "";
  try {
    const [cardResult, pageResult] = await Promise.all([
      intakeService.getPatientInsuranceCards(patientId),
      intakeService.getPatientDocumentPages(patientId, undefined, route.params.id),
    ]);
    if (stale()) return false;
    intakeStore.insuranceCards = cardResult.results.map((r) => new IntakeInsuranceCard(r, dateAdapter));
    const allPages = pageResult.results
      .map((p) => new IntakeDocumentPage(p, dateAdapter))
      .sort((a, b) =>
        a.documentKey === b.documentKey
          ? (a.pageNumber ?? 0) - (b.pageNumber ?? 0)
          : a.documentKey.localeCompare(b.documentKey)
      );
    pageChanger.pages = allPages.length ? allPages : insuranceCards.value.map((card) => card.pages[0]);
    // Land on the active (or first) insurance card's page; all patient pages remain navigable.
    const initialCard =
      insuranceCards.value.find((card) => card.id === activeInsuranceCardId.value) ?? insuranceCards.value[0];
    if (initialCard) {
      pageChanger.goToDataFormPage(initialCard);
    }
    currentDocumentPage.value = pageChanger.currentPageRef;
    return true;
  } catch (e) {
    if (!stale()) errorMsg.value = `Failed to fetch patient documents: ${e.message || e}`;
    return false;
  }
};

const onPatientGuessHighlight = (data) => {
  previewRef.value?.onHighlightBoundingBox(data, intakeOrder.value?.orderForm);
};

const isMissing = (e) => [403, 404].includes(e?.response?.status);

const fetchBenefitsStep = async () => {
  try {
    return await intakeService.getIntakeOrderBenefitsStep(route.params.id, route.query.bv);
  } catch (e) {
    if (route.query.bv || e?.response?.status !== 404) throw e;
    return null;
  }
};

const fetchIntakeOrder = async (stale = () => false) => {
  errorMsg.value = "";
  pageError.value = null;
  try {
    const [result, step] = await Promise.all([intakeService.getIntakeOrder(route.params.id), fetchBenefitsStep()]);
    if (stale()) return;
    bvStep.value = step;
    intakeStore.intakeOrder = new IntakeOrder(result, dateAdapter);
    patientInfo.value = new Patient(result.patient, dateAdapter);
    const patientId = intakeOrder.value.patient.id;
    activeInsuranceCardId.value = intakeOrder.value.insuranceCard?.id;
    const cardsFetched = isManualEntry.value ? true : await fetchPatientQualifyDocuments(patientId, stale);
    if (stale()) return;
    const pickCard =
      !activeInsuranceCardId.value && !coverageSelected.value && !isReadOnlyDevice.value && !earlierStep.value;
    if (cardsFetched && pickCard && insuranceCards.value.length === 1) {
      await markAsActiveInsurance(insuranceCards.value[0]);
      if (stale()) return;
    }
    updatePayorInNetwork();
  } catch (e) {
    if (stale()) return;
    if (isMissing(e)) pageError.value = "This benefit verification was not found.";
    else if (route.query.bv) pageError.value = "This benefit verification could not be loaded.";
    else errorMsg.value = `Error fetching the Draft Order: ${e.message || e}`;
  } finally {
    if (!stale()) showLoader.value = false;
  }
};

const goBack = () => {
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const errorWrapper = async (fn, errorPrefix) => {
  showLoader.value = true;
  try {
    await fn();
    return true;
  } catch (e) {
    errorMsg.value = `${errorPrefix} ${e.message}`;
  } finally {
    showLoader.value = false;
  }
  return false;
};

const onSnooze = async ({ minutes }) => {
  await errorWrapper(async () => {
    intakeStore.intakeOrder = await intakeActions.onSnooze({ orderId: intakeOrder.value.id, minutes });
  }, "Error snoozing the order");
};

const patientName = computed(() =>
  [patientInfo.value?.firstName, patientInfo.value?.lastName].filter(Boolean).join(" ")
);

const orderDrugName = computed(() => drugNameOf(intakeOrder.value?.orderForm));

// This order closed as merged, so the work carries on at the order that kept it.
const onMerged = (destinationId) => {
  router.push(stepRoute("benefits_verification", destinationId));
};

const onArchive = async () => {
  if (intakeOrder.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      showLoader.value = true;
      await intakeService.deleteArchiveOrder(intakeOrder.value.id);
    } catch (e) {
      errorMsg.value = `Error unarchiving the order: ${e.message}`;
    } finally {
      showLoader.value = false;
      archiveReasonDialog.value = false;
    }
  } else {
    archiveReasonDialog.value = true;
  }
};

const onArchived = () => {
  archiveReasonDialog.value = false;
  router.push(listReturnTarget({ name: "all-patients", query: filtersStore.getNavigationQuery() }));
};

const shouldShowReimbursementTable = computed(() => {
  // LAM-4074: hidden for all clients; restore the reimbursementTableEnabled config check to re-enable.
  return false;
});
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
